import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { adminLogin } from '@/utils/api';
import { toast } from 'sonner';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async () => {
    if (!username || !password) {
      toast.error('Please enter both username and password');
      return;
    }
    setLoading(true);
    const result = await adminLogin(username, password);
    setLoading(false);
    if (result?.token) {
      toast.success('Welcome, Admin!');
      navigate('/admin/dashboard');
    } else {
      toast.error('Invalid credentials');
    }
  };

  return (
    <div 
      data-testid="admin-login-container"
      className="min-h-screen flex items-center justify-center bg-[#050505] relative overflow-hidden"
    >
      {/* Animated background */}
      <motion.div
        className="absolute top-20 right-20 w-64 h-64 bg-[#FFB800]/5 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 5, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-64 h-64 bg-[#007AFF]/5 rounded-full blur-3xl"
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 5, repeat: Infinity, delay: 2 }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 backdrop-blur-xl bg-black/60 border border-white/10 rounded-2xl p-10 w-full max-w-md shadow-[0_0_50px_rgba(255,184,0,0.1)]"
      >
        <div className="flex flex-col items-center gap-6">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#FFB800] to-[#FF8800] flex items-center justify-center shadow-[0_0_30px_rgba(255,184,0,0.4)]"
          >
            <Shield className="w-10 h-10 text-black" />
          </motion.div>

          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-white mb-2">Admin Portal</h1>
            <p className="text-[#A1A1AA] font-body text-sm">DSA With Kapil · Instructor Access</p>
          </div>

          <div className="w-full space-y-4">
            <div>
              <label className="text-[#71717A] text-xs font-code mb-2 block">USERNAME</label>
              <Input
                data-testid="admin-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter admin username"
                className="bg-[#1A1A20] border-white/20 text-white font-code focus:border-[#FFB800]"
              />
            </div>
            <div>
              <label className="text-[#71717A] text-xs font-code mb-2 block">PASSWORD</label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="Enter admin password"
                className="bg-[#1A1A20] border-white/20 text-white font-code focus:border-[#FFB800]"
              />
            </div>

            <Button
              data-testid="admin-login-button"
              onClick={handleLogin}
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-[#FFB800] to-[#FF8800] hover:from-[#FFC520] hover:to-[#FF9920] text-black font-heading font-semibold text-base shadow-[0_4px_20px_rgba(255,184,0,0.3)]"
            >
              {loading ? 'Signing in...' : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Sign In
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>

          <button
            onClick={() => navigate('/')}
            className="text-[#71717A] text-sm font-body hover:text-white transition-colors"
          >
            ← Back to Student App
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
